/**
 * Live prices, read from Stripe via the `get-pricing` edge function.
 *
 * Why this exists: "£39.99" used to be a hardcoded string here AND on the
 * marketing pricing page, read from nothing. Changing what you charge meant
 * editing two React files and hoping you found both — and for a while the
 * sandbox price was £28.99 against a displayed £39.99, with nothing to catch it.
 *
 * Now the displayed figure is whatever Stripe says it is. Change a price in the
 * Stripe dashboard and this follows within the endpoint's cache window, no
 * deploy.
 *
 * The caller supplies a fallback (the old hardcoded copy). It shows during the
 * fetch and if the fetch fails, so the pricing UI never renders blank or with a
 * "—" where a price should be. A stale-but-plausible price beats a hole in the
 * page; anything actually charged comes from Stripe server-side regardless, so
 * a stale label can never cause a wrong charge.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface TierPricing {
    tier: string;
    priceId: string;
    unitAmount: number | null;
    currency: string;
    interval: string | null;
    productName: string | null;
    /** Preformatted for display, e.g. "£39.99". */
    formatted: string | null;
    maxMembers: number;
}

export interface PricingResult {
    /** Tier key -> pricing. Empty until the first successful load. */
    prices: Record<string, TierPricing>;
    loading: boolean;
    /** Tier keys Stripe had no active price for. Worth surfacing in dev. */
    missing: string[];
    error: string | null;
}

// Module-level cache. The settings page and any other consumer mounting in the
// same session share one network call.
let cached: { at: number; data: Omit<PricingResult, 'loading'> } | null = null;
const TTL_MS = 5 * 60 * 1000;

export function usePricing(): PricingResult {
    const [state, setState] = useState<Omit<PricingResult, 'loading'>>(
        () => cached?.data ?? { prices: {}, missing: [], error: null },
    );
    const [loading, setLoading] = useState(!cached);

    useEffect(() => {
        if (cached && Date.now() - cached.at < TTL_MS) {
            setState(cached.data);
            setLoading(false);
            return;
        }
        let cancelled = false;

        (async () => {
            try {
                // invoke() POSTs, which sidesteps the preflight quirk a
                // cross-origin GET with an apikey header would hit.
                const { data, error } = await supabase.functions.invoke('get-pricing');
                if (error) throw error;
                const next = {
                    prices: (data?.tiers ?? {}) as Record<string, TierPricing>,
                    missing: (data?.missing ?? []) as string[],
                    error: null as string | null,
                };
                cached = { at: Date.now(), data: next };
                if (!cancelled) setState(next);
            } catch (err) {
                // Deliberately not surfaced to the user — the caller's fallback
                // copy renders instead. Logged so it is visible in Sentry.
                console.error('[usePricing] falling back to static copy:', err);
                if (!cancelled) {
                    setState((s) => ({ ...s, error: (err as Error)?.message ?? 'unknown' }));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    return { ...state, loading };
}

/** The live price for a tier, or the caller's fallback if it is not loaded yet. */
export function priceFor(
    prices: Record<string, TierPricing>,
    tier: string,
    fallback: string,
): string {
    return prices[tier]?.formatted ?? fallback;
}

/** "/mo" or "/yr", matching whatever interval Stripe has on the price. */
export function periodFor(
    prices: Record<string, TierPricing>,
    tier: string,
    fallback: string,
): string {
    const interval = prices[tier]?.interval;
    if (!interval) return fallback;
    return interval === 'year' ? '/yr' : interval === 'month' ? '/mo' : `/${interval}`;
}
